import { CommonModule } from '@angular/common';
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { AxsIconComponent } from '@vanguard/axs-angular';
import { Subject, takeUntil } from 'rxjs';
import { ContentService } from 'src/app/shared/services/content/content.service';
import { SharedInfoService } from 'src/app/shared/services/shared-info/shared-info.service';
import { content } from 'src/assets/content/fallback-content';

import { AdobeLaunchService } from '../../adobe-launch/adobe-launch.service';
import { AxsInputNumberComponent } from '../../shared/axs/input-number/input-number.component';
import { GatekeeperService } from '../../shared/services/gatekeeper/gatekeeper.service';
import { ClearAllFieldsButtonComponent } from '../clear-all-fields-button/clear-all-fields-button.component';

interface FilingStatusOption {
  label: string;
  value: string;
}
interface AxsSelectChangeDetail {
  value: string;
  label: string;
}
interface StateTaxFlags {
  progressiveTaxRate: boolean;
  iraTaxable: boolean;
}

// Used until the first TXS fetch resolves, and if it fails. Both notes stay hidden rather
// than asserting something wrong about a client's state tax treatment.
const DEFAULT_STATE_TAX_FLAGS: StateTaxFlags = { progressiveTaxRate: false, iraTaxable: true };

@Component({
  selector: 'client-tax-info',
  standalone: true,
  imports: [AxsInputNumberComponent, AxsIconComponent, ClearAllFieldsButtonComponent, CommonModule],
  templateUrl: './client-tax-info.component.html',
  styleUrl: './client-tax-info.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ClientTaxInfoComponent implements OnInit, OnDestroy {
  private readonly unsubscribe = new Subject<void>();

  @Output() infoIconClicked = new EventEmitter<{
    accordion: string;
    fieldId: string;
    highlightOverride?: string;
  }>();

  content = content.Data.v4_rcc_root.content.clientInfoTax;
  rothConvDetailsContent = content.Data.v4_rcc_root.content.rothConvDetails;
  errorsContent = content.Data.v4_rcc_root.content.error;
  stateTaxRatesFromBXR = false;

  _currentStateTaxFlags: StateTaxFlags = DEFAULT_STATE_TAX_FLAGS;
  _futureStateTaxFlags: StateTaxFlags = DEFAULT_STATE_TAX_FLAGS;

  _currentIncome: any = 0;
  _currentFilingStatus: string = 'single';
  _currentFederalTax: any = 0;
  _currentStateTax: any = 0;
  _currentStateTaxStatic: any = 0;
  _currentLocalTax: any = 0;
  _currentStateOfTaxation: string = 'NC';

  _futureIncome: any = 0;
  _futureFilingStatus = 'single';
  _futureFederalTax: any = 0;
  _futureStateTax: any = 0;
  _futureStateTaxStatic: any = 0;
  _futureLocalTax: any = 0;
  _futureStateOfTaxation: string = 'NC';

  // Each default is mirrored into the service, which compares the field against it to decide whether
  // to send BXR an override. A blank default (cleared fields) means "no default known".
  private _defaultCurrentFederalTax = '';
  private _defaultFutureFederalTax = '';
  private _defaultCurrentStateTaxStatic = '0';
  private _defaultFutureStateTaxStatic = '0';

  get defaultCurrentFederalTax(): string {
    return this._defaultCurrentFederalTax;
  }
  set defaultCurrentFederalTax(value: string) {
    this._defaultCurrentFederalTax = value;
    this.sharedInfoService.setRateDefault('currentFederal', value === '' ? null : Number(value));
  }

  get defaultFutureFederalTax(): string {
    return this._defaultFutureFederalTax;
  }
  set defaultFutureFederalTax(value: string) {
    this._defaultFutureFederalTax = value;
    this.sharedInfoService.setRateDefault('futureFederal', value === '' ? null : Number(value));
  }

  get defaultCurrentStateTaxStatic(): string {
    return this._defaultCurrentStateTaxStatic;
  }
  set defaultCurrentStateTaxStatic(value: string) {
    this._defaultCurrentStateTaxStatic = value;
    this.sharedInfoService.setRateDefault('currentState', value === '' ? null : Number(value));
  }

  get defaultFutureStateTaxStatic(): string {
    return this._defaultFutureStateTaxStatic;
  }
  set defaultFutureStateTaxStatic(value: string) {
    this._defaultFutureStateTaxStatic = value;
    this.sharedInfoService.setRateDefault('futureState', value === '' ? null : Number(value));
  }

  filingStatusOptions: FilingStatusOption[] = [];
  // US states + DC only. TXS has no tax data for territories (AS, FM, GU, MH, MP,
  // PW, PR, VI) — confirmed with the TXS team — so they're excluded here rather
  // than letting a user pick one and silently show the previous state's rate.
  readonly stateOfTaxationValues: readonly string[] = [
    'AK',
    'AL',
    'AR',
    'AZ',
    'CA',
    'CO',
    'CT',
    'DC',
    'DE',
    'FL',
    'GA',
    'HI',
    'IA',
    'ID',
    'IL',
    'IN',
    'KS',
    'KY',
    'LA',
    'MA',
    'MD',
    'ME',
    'MI',
    'MN',
    'MO',
    'MS',
    'MT',
    'NC',
    'ND',
    'NE',
    'NH',
    'NJ',
    'NM',
    'NV',
    'NY',
    'OH',
    'OK',
    'OR',
    'PA',
    'RI',
    'SC',
    'SD',
    'TN',
    'TX',
    'UT',
    'VA',
    'VT',
    'WA',
    'WI',
    'WV',
    'WY',
  ];

  incomeHelperText = '';

  // Income + Roth conversion amount, published by SharedInfoService. Treats a blank or invalid field as
  // zero, which the last known-good subjects cannot express.
  _combinedCurrentIncome = 0;

  // Error states
  currentIncomeError = false;
  currentFederalTaxError = false;
  currentStateTaxError = false;
  _currentFederalTaxErrorMessage: string = '';
  _currentStateTaxErrorMessage: string = '';

  futureIncomeError = false;
  futureFederalTaxError = false;
  futureStateTaxError = false;
  _futureFederalTaxErrorMessage: string = '';
  _futureStateTaxErrorMessage: string = '';

  // Adobe Analytics names
  currentIncomeAdobeName = 'Current Annual Income';
  currentFederalTaxAdobeName = 'Current federal tax rate';
  currentStateTaxAdobeName = 'Current state and local tax rate';
  futureIncomeAdobeName = 'Future Income';
  futureFederalTaxAdobeName = 'Future federal tax rate';
  futureStateTaxAdobeName = 'Future state and local tax rate';

  // eslint-disable-next-line max-params
  constructor(
    public sharedInfoService: SharedInfoService,
    private readonly adobeLaunchService: AdobeLaunchService,
    private readonly gatekeeperService: GatekeeperService,
    private readonly contentService: ContentService,
  ) {
    this.contentService.getContent().subscribe((data) => {
      this.content = data.Data.v4_rcc_root.content.clientInfoTax;
      this.rothConvDetailsContent = data.Data.v4_rcc_root.content.rothConvDetails;
      this.errorsContent = data.Data.v4_rcc_root.content.error;
    });
  }

  ngOnInit(): void {
    this.stateTaxRatesFromBXR = this.gatekeeperService.getToggleValue('STATE_TAX_RATES');
    this.initializeFilingStatusOptions();
    this.initializeValues();
    this.setIncomeHelperText();
  }

  private initializeFilingStatusOptions(): void {
    this.filingStatusOptions = [
      { label: this.content?.single, value: 'single' },
      { label: this.content?.marriedjointly, value: 'married-filing-jointly' },
      { label: this.content?.marriedseparately, value: 'married-filing-separately' },
      { label: this.content?.headofhouseholds, value: 'head-of-household' },
    ];
  }

  // eslint-disable-next-line max-statements
  initializeValues() {
    // Subscribe to sample client load events to clear all errors and refresh helper text
    this.sharedInfoService
      .getSampleClientLoaded$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(() => {
        this.clearAllErrors();
        this.setIncomeHelperText();

        // Sample clients carry no state/local tax rate, so treat a client switch as a fresh load
        this._currentStateTaxStatic = 0;
        this.defaultCurrentStateTaxStatic = '0';
        this._futureStateTaxStatic = 0;
        this.defaultFutureStateTaxStatic = '0';
      });

    // Desktop and the mobile sheet each render this component, so both have to blank on either's click.
    this.sharedInfoService
      .getInputsCleared$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((section) => {
        if (section === 'clientTax') {
          this.blankFields();
        }
      });

    // Combined income is owned by RothConvDetailsComponent's field as much as this one, so it arrives via
    // the service. Display only - never write back from here, or the emission re-enters as a loop.
    this.sharedInfoService
      .getCombinedCurrentIncome$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((value) => {
        this._combinedCurrentIncome = value;
        this.setIncomeHelperText();
      });

    this.sharedInfoService
      .getCurrentIncome$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((value) => {
        this._currentIncome = value;
      });
    this.sharedInfoService
      .getFilingStatus$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((value) => {
        this._currentFilingStatus = value;
      });
    this.sharedInfoService
      .getCurrentFederalTaxRate$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((value) => {
        this._currentFederalTax = value;
        const calculatedRate = this.sharedInfoService.getCurrentTaxRateBasedOnCurrentYear();
        this.defaultCurrentFederalTax = calculatedRate.toString();
      });
    this.sharedInfoService
      .getCurrentStateTaxRate$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((value) => {
        this._currentStateTax = value;
      });
    this.sharedInfoService
      .getCurrentStateOfTaxation$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((value) => {
        this._currentStateOfTaxation = value;
      });
    // Per-state notes and the state rate come from TXS. Until they arrive the notes stay
    // hidden via the default, and the rate keeps whatever it already showed.
    this.sharedInfoService
      .getCurrentTaxRates$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((taxRates) => {
        this._currentStateTaxFlags = taxRates
          ? {
              progressiveTaxRate: taxRates.stateIsProgressive,
              iraTaxable: taxRates.stateTaxesRothConversion,
            }
          : DEFAULT_STATE_TAX_FLAGS;
        this.applyStateTaxRate(false);
        if (taxRates && this.sharedInfoService.canDeriveFederalRate(false)) {
          this.refreshFederalTaxRate(false);
        }
      });
    this.sharedInfoService
      .getFutureTaxRates$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((taxRates) => {
        this._futureStateTaxFlags = taxRates
          ? {
              progressiveTaxRate: taxRates.stateIsProgressive,
              iraTaxable: taxRates.stateTaxesRothConversion,
            }
          : DEFAULT_STATE_TAX_FLAGS;
        this.applyStateTaxRate(true);
        if (taxRates && this.sharedInfoService.canDeriveFederalRate(true)) {
          this.refreshFederalTaxRate(true);
        }
      });
    // The state default is income-based, so it follows income and conversion edits too. Both
    // streams re-emit on any of those changes; before brackets load this is a no-op.
    this.sharedInfoService
      .getCombinedCurrentIncome$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(() => this.applyStateTaxRate(false));
    this.sharedInfoService
      .getCombinedFutureIncome$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(() => this.applyStateTaxRate(true));
    this.sharedInfoService
      .getCurrentStateTaxStatic$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((value) => {
        this._currentStateTaxStatic = value;
      });
    this.sharedInfoService
      .getCurrentLocalTax$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((value) => {
        this._currentLocalTax = value;
      });
    this.sharedInfoService
      .getFutureIncome$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((value) => {
        this._futureIncome = value;
      });
    this.sharedInfoService
      .getFutureFilingStatus$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((value) => {
        this._futureFilingStatus = value;
      });
    this.sharedInfoService
      .getFutureFederalTaxRate$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((value) => {
        this._futureFederalTax = value;
        const calculatedRate = this.sharedInfoService.getFutureTaxRateBasedOnCurrentYear();
        this.defaultFutureFederalTax = calculatedRate.toString();
      });
    this.sharedInfoService
      .getFutureStateTaxRate$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((value) => {
        this._futureStateTax = value;
      });
    this.sharedInfoService
      .getFutureStateOfTaxation$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((value) => {
        this._futureStateOfTaxation = value;
      });
    this.sharedInfoService
      .getFutureStateTaxStatic$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((value) => {
        this._futureStateTaxStatic = value;
      });
    this.sharedInfoService.getFutureLocalTax$().subscribe((value) => {
      this._futureLocalTax = value;
    });
  }

  private clearAllErrors(): void {
    // Clear all error states
    this.currentIncomeError = false;
    this.currentFederalTaxError = false;
    this.currentStateTaxError = false;
    this.futureIncomeError = false;
    this.futureFederalTaxError = false;
    this.futureStateTaxError = false;

    // Clear all error messages
    this._currentFederalTaxErrorMessage = '';
    this._currentStateTaxErrorMessage = '';
    this._futureFederalTaxErrorMessage = '';
    this._futureStateTaxErrorMessage = '';
  }

  onClearAllFields(): void {
    this.sharedInfoService.clearSection('clientTax');
    this.adobeLaunchService.captureCtaButton('client-calculator:clear-all-fields', 'betr calculator');
  }

  // eslint-disable-next-line max-statements
  private blankFields(): void {
    this.clearAllErrors();

    this._currentIncome = '';
    this._currentFilingStatus = '';
    this._currentFederalTax = '';
    this._currentStateTax = '';
    this._currentStateOfTaxation = '';
    this._currentStateTaxStatic = '';
    this._currentLocalTax = '';
    this._futureIncome = '';
    this._futureFilingStatus = '';
    this._futureFederalTax = '';
    this._futureStateTax = '';
    this._futureStateOfTaxation = '';
    this._futureStateTaxStatic = '';
    this._futureLocalTax = '';

    // Blank defaults keep the custom-override notes hidden until the field is filled again.
    this.defaultCurrentFederalTax = '';
    this.defaultFutureFederalTax = '';
    this.defaultCurrentStateTaxStatic = '';
    this.defaultFutureStateTaxStatic = '';

    this._currentStateTaxFlags = DEFAULT_STATE_TAX_FLAGS;
    this._futureStateTaxFlags = DEFAULT_STATE_TAX_FLAGS;
    this.setIncomeHelperText();
  }

  currentIncomeClicked() {
    this.adobeLaunchService.captureAction(
      'client-calculator:annual-taxable-income-after-deductions',
      'input',
      'betr calculator',
    );
  }

  onCurrentIncomeChange(value: number): void {
    this._currentIncome = value;
    this.currentIncomeError = isNaN(value) || value < 1 || value > 10000000;

    // A blank or out-of-range field counts as zero income for bracket selection and the combined-income
    // hint. Must be set before the rate lookups below, which read it.
    this.sharedInfoService.setEffectiveCurrentIncome(this.currentIncomeError ? 0 : value);

    if (this.currentIncomeError) {
      this.sharedInfoService.addInputError('Annual taxable income after deductions');
      this.adobeLaunchService.trackError(
        AdobeLaunchService.errorCodes.rangeError,
        this.currentIncomeAdobeName,
      );
    } else {
      this.sharedInfoService.removeInputError('Annual taxable income after deductions');
      this.sharedInfoService.setCurrentIncome(value);

      // Auto-calculate federal tax rate based on income and filing status (uses current year)
      if (this.sharedInfoService.canDeriveFederalRate(false)) {
        this._currentFederalTax = this.sharedInfoService.getCurrentTaxRateBasedOnCurrentYear();
        this.defaultCurrentFederalTax = this._currentFederalTax.toString();
        this.sharedInfoService.setCurrentFederalTaxRate(this._currentFederalTax);
      }

      // Auto-calculate dividend tax rate based on income (uses current year)
      this.sharedInfoService.setUpdatedDividendTaxRateBasedOnCurrentYear();
    }

    // Recomputed in both branches so an invalid field cannot leave a stale figure on screen.
    this.setIncomeHelperText();
    this.adobeLaunchService.captureAction('task complete', 'text', 'betr calculator');
  }

  onCurrentFilingStatusChange(event: { value: string }): void {
    // Read the override before setFilingStatus, since the rate the fetch returns for the new
    // status may coincide with the user's value and make the override undetectable afterwards.
    const wasOverridden = this.currentFederalTaxOverridden;
    this.sharedInfoService.setFilingStatus(event.value);

    // Provisional rate from the brackets currently in hand; the tax-rates subscription
    // corrects it once the new filing status's brackets arrive.
    if (this.sharedInfoService.canDeriveFederalRate(false)) {
      const rate = this.sharedInfoService.getCurrentTaxRateBasedOnCurrentYear();
      this.defaultCurrentFederalTax = rate.toString();
      if (!wasOverridden) {
        this._currentFederalTax = rate;
        this.sharedInfoService.setCurrentFederalTaxRate(rate);
      }
    }

    // Auto-calculate dividend tax rate (uses current year)
    this.sharedInfoService.setUpdatedDividendTaxRateBasedOnCurrentYear();

    this.setIncomeHelperText();
  }

  onCurrentFederalTaxChange(value: number): void {
    this._currentFederalTax = value;
    this.adobeLaunchService.captureAction('current-federal-tax', 'text');

    if (isNaN(value) || value < 1 || value > 100) {
      this._currentFederalTaxErrorMessage = this.errorsContent?.['federaltaxrangeerror'];
      this.currentFederalTaxError = true;
    } else {
      this._currentFederalTaxErrorMessage = '';
      this.currentFederalTaxError = false;
    }

    if (this.currentFederalTaxError) {
      this.sharedInfoService.addInputError('Federal tax rate');
      this.adobeLaunchService.trackError(
        AdobeLaunchService.errorCodes.rangeError,
        this.currentFederalTaxAdobeName,
      );
    } else {
      this.sharedInfoService.setCurrentFederalTaxRate(value);
      this.sharedInfoService.removeInputError('Federal tax rate');
    }
  }

  onCurrentStateTaxChange(value: number): void {
    this._currentStateTax = value;
    this.adobeLaunchService.captureAction('current-state-tax', 'text');

    if (isNaN(value) || value < 0 || value > 100) {
      this.sharedInfoService.addInputError('State & local tax rate');
      this.currentStateTaxError = true;
      this._currentStateTaxErrorMessage = this.errorsContent?.['currentstatetaxerror'];
      this.adobeLaunchService.trackError(
        AdobeLaunchService.errorCodes.rangeError,
        this.currentStateTaxAdobeName,
      );
    } else {
      this.sharedInfoService.removeInputError('State & local tax rate');
      this.currentStateTaxError = false;
      this.sharedInfoService.setCurrentStateTaxRate(value);
    }
  }

  onCurrentLocalTaxChange(value: number): void {
    this._currentLocalTax = value;
    this.sharedInfoService.setCurrentStateTaxRate(
      Number(this._currentStateTaxStatic) + Number(this._currentLocalTax),
    );
    this.sharedInfoService.setCurrentLocalTax(value);
  }

  onCurrentStateTaxStaticChange(value: number): void {
    this._currentStateTaxStatic = value;
    this.sharedInfoService.setCurrentStateTaxRate(
      Number(this._currentStateTaxStatic) + Number(this._currentLocalTax),
    );
    this.sharedInfoService.setCurrentStateTaxStatic(value);
  }

  onCurrentStateOfTaxationChange(event: Event): void {
    const selectedValue = this.getSelectedStateFromChangeEvent(event);
    if (typeof selectedValue === 'string' && selectedValue.length > 0) {
      this._currentStateOfTaxation = selectedValue;
      this.sharedInfoService.setCurrentStateOfTaxation(selectedValue);
    }
  }

  onCurrentStateOptionSelect(stateCode: string): void {
    this._currentStateOfTaxation = stateCode;
    this.sharedInfoService.setCurrentStateOfTaxation(stateCode);
  }

  futureIncomeClicked() {
    this.adobeLaunchService.captureAction(
      'client-calculator:taxable-income-at-distribution',
      'input',
      'betr calculator',
    );
  }

  onFutureIncomeChange(value: number): void {
    this._futureIncome = value;
    this.futureIncomeError = isNaN(value) || value < 1 || value > 10000000;

    // See onCurrentIncomeChange: zero while unusable, and set before the rate lookups that read it.
    this.sharedInfoService.setEffectiveFutureIncome(this.futureIncomeError ? 0 : value);

    if (this.futureIncomeError) {
      this.sharedInfoService.addInputError('Taxable income at distribution');
      this.adobeLaunchService.trackError(
        AdobeLaunchService.errorCodes.rangeError,
        this.futureIncomeAdobeName,
      );
    } else {
      this.sharedInfoService.removeInputError('Taxable income at distribution');
      this.sharedInfoService.setFutureIncome(value);

      // Auto-calculate future federal tax rate based on income and filing status (uses current year)
      if (this.sharedInfoService.canDeriveFederalRate(true)) {
        this._futureFederalTax = this.sharedInfoService.getFutureTaxRateBasedOnCurrentYear();
        this.defaultFutureFederalTax = this._futureFederalTax.toString();
        this.sharedInfoService.setFutureFederalTaxRate(this._futureFederalTax);
      }

      // Auto-calculate capital gains tax rate based on future income (uses current year)
      this.sharedInfoService.setUpdatedCapitalGainsTaxRateBasedOnCurrentYear();
    }

    this.adobeLaunchService.captureAction('task complete', 'text', 'betr calculator');
  }

  onFutureFilingStatusChange(event: { value: string }): void {
    const wasOverridden = this.futureFederalTaxOverridden;
    this.sharedInfoService.setFutureFilingStatus(event.value);

    // Provisional; the tax-rates subscription corrects it once the new brackets arrive.
    if (this.sharedInfoService.canDeriveFederalRate(true)) {
      const rate = this.sharedInfoService.getFutureTaxRateBasedOnCurrentYear();
      this.defaultFutureFederalTax = rate.toString();
      if (!wasOverridden) {
        this._futureFederalTax = rate;
        this.sharedInfoService.setFutureFederalTaxRate(rate);
      }
    }

    // Auto-calculate capital gains tax rate (uses current year)
    this.sharedInfoService.setUpdatedCapitalGainsTaxRateBasedOnCurrentYear();
  }

  onFutureFederalTaxChange(value: number): void {
    this._futureFederalTax = value;
    this.adobeLaunchService.captureAction('future-federal-tax', 'text');

    if (isNaN(value) || value < 1 || value > 100) {
      this.sharedInfoService.addInputError('Federal tax rate');
      this.futureFederalTaxError = true;
      this._futureFederalTaxErrorMessage = this.errorsContent?.['federaltaxrangeerror'];
      this.adobeLaunchService.trackError(
        AdobeLaunchService.errorCodes.rangeError,
        this.futureFederalTaxAdobeName,
      );
    } else {
      this.sharedInfoService.removeInputError('Federal tax rate');
      this.futureFederalTaxError = false;
      this.sharedInfoService.setFutureFederalTaxRate(value);
    }
  }

  onFutureStateTaxChange(value: number): void {
    this._futureStateTax = value;
    this.adobeLaunchService.captureAction('future-state-tax', 'text');

    if (isNaN(value) || value < 0 || value > 100) {
      this.sharedInfoService.addInputError('State & local tax rate');
      this.futureStateTaxError = true;
      this._futureStateTaxErrorMessage = this.errorsContent?.['currentstatetaxerror'];
      this.adobeLaunchService.trackError(
        AdobeLaunchService.errorCodes.rangeError,
        this.futureStateTaxAdobeName,
      );
    } else {
      this.sharedInfoService.removeInputError('State & local tax rate');
      this.futureStateTaxError = false;
      this.sharedInfoService.setFutureStateTaxRate(value);
    }
  }

  onFutureLocalTaxChange(value: number): void {
    this._futureLocalTax = value;
    this.sharedInfoService.setFutureStateTaxRate(
      Number(this._futureStateTaxStatic) + Number(this._futureLocalTax),
    );
    this.sharedInfoService.setFutureLocalTax(value);
  }

  onFutureStateTaxStaticChange(value: number): void {
    this._futureStateTaxStatic = value;
    this.sharedInfoService.setFutureStateTaxRate(
      Number(this._futureStateTaxStatic) + Number(this._futureLocalTax),
    );
    this.sharedInfoService.setFutureStateTaxStatic(value);
  }

  onFutureStateOfTaxationChange(event: Event): void {
    const selectedValue = this.getSelectedStateFromChangeEvent(event);
    if (typeof selectedValue === 'string' && selectedValue.length > 0) {
      this._futureStateOfTaxation = selectedValue;
      this.sharedInfoService.setFutureStateOfTaxation(selectedValue);
    }
  }

  onFutureStateOptionSelect(stateCode: string): void {
    this._futureStateOfTaxation = stateCode;
    this.sharedInfoService.setFutureStateOfTaxation(stateCode);
  }

  private getSelectedStateFromChangeEvent(event: Event): string | undefined {
    return (event as CustomEvent<AxsSelectChangeDetail>)?.detail?.value;
  }

  private formatCurrency(amount: number): string {
    return amount.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }

  setIncomeHelperText(): void {
    // Any figure here would describe an income the user hasn't entered.
    if (this.sharedInfoService.isInputCleared('currentIncome')) {
      this.incomeHelperText = '';
      return;
    }

    // Behind the toggle, the distance-to-next-bracket hint moves to the Roth conversion amount field and
    // this one shows the combined figure instead.
    if (this.stateTaxRatesFromBXR) {
      this.incomeHelperText = (this.content?.['combinedIncomeHelpText'] ?? '').replace(
        '$XXX',
        this.formatCurrency(this._combinedCurrentIncome),
      );
      return;
    }

    const amount = this.sharedInfoService.getAmountUntilNextTaxBracketBasedOnCurrentYear();
    const highestBracketText = this.content?.['highesttaxbracket'];
    const nextBracketText = this.rothConvDetailsContent?.['rothConvCashLabel'];

    if (amount === null) {
      this.incomeHelperText = highestBracketText;
    } else {
      this.incomeHelperText = `${this.formatCurrency(amount)} ${nextBracketText}`;
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  infoIconOnClick(accordion: string, fieldId: string, highlightOverride?: string): void {
    // if highlightOverride is provided, the accordion scrolls to the ID of fieldId, but highlights only the ID of the override
    this.infoIconClicked.emit({ accordion, fieldId, highlightOverride });
    this.adobeLaunchService.captureAction(
      'client-calculator:terms-and-definitions',
      'link',
      'betr calculator',
    );
  }

  // Error message getters
  get currentIncomeErrorMessage(): string {
    return this.currentIncomeError ? this.errorsContent?.['currentincomeerror'] : '';
  }

  get currentFederalTaxErrorMessage(): string {
    return this.currentFederalTaxError ? this._currentFederalTaxErrorMessage : '';
  }

  get currentStateTaxErrorMessage(): string {
    return this.currentStateTaxError ? this._currentStateTaxErrorMessage : '';
  }

  get futureIncomeErrorMessage(): string {
    return this.futureIncomeError ? this.errorsContent?.['currentincomeerror'] : '';
  }

  get futureFederalTaxErrorMessage(): string {
    return this.futureFederalTaxError ? this._futureFederalTaxErrorMessage : '';
  }

  get futureStateTaxErrorMessage(): string {
    return this.futureStateTaxError ? this._futureStateTaxErrorMessage : '';
  }

  get rateDiffersText(): string {
    // Disable rate differ text built into text input component on toggle - the new warning design has bold text (can't be done in old scheme)
    return this.stateTaxRatesFromBXR ? '' : this.errorsContent.rateDiffers;
  }

  get rateDiffersLinkText(): string {
    return this.stateTaxRatesFromBXR ? '' : this.errorsContent.rateDiffersLink;
  }

  get currentFederalTaxOverridden(): boolean {
    return (
      !this.currentFederalTaxError &&
      Number(this._currentFederalTax) !== Number(this.defaultCurrentFederalTax)
    );
  }

  get futureFederalTaxOverridden(): boolean {
    return (
      !this.futureFederalTaxError &&
      Number(this._futureFederalTax) !== Number(this.defaultFutureFederalTax)
    );
  }

  revertCurrentFederalTax(): void {
    const defaultRate = this.sharedInfoService.getCurrentTaxRateBasedOnCurrentYear();
    this._currentFederalTax = defaultRate;
    this.defaultCurrentFederalTax = defaultRate.toString();
    this.sharedInfoService.setCurrentFederalTaxRate(defaultRate);
    this.adobeLaunchService.captureAction('revert-current-federal-tax', 'link');
  }

  revertFutureFederalTax(): void {
    const defaultRate = this.sharedInfoService.getFutureTaxRateBasedOnCurrentYear();
    this._futureFederalTax = defaultRate;
    this.defaultFutureFederalTax = defaultRate.toString();
    this.sharedInfoService.setFutureFederalTaxRate(defaultRate);
    this.adobeLaunchService.captureAction('revert-future-federal-tax', 'link');
  }

  /**
   * Re-reads the federal rate from the freshly fetched brackets. The filing-status handler
   * computes it synchronously, before the debounced fetch has landed, so without this the
   * field keeps the previous status's answer. An overridden rate is left alone — only the
   * default it reverts to moves.
   */
  private refreshFederalTaxRate(isFuture: boolean): void {
    if (isFuture) {
      const wasOverridden = this.futureFederalTaxOverridden;
      const rate = this.sharedInfoService.getFutureTaxRateBasedOnCurrentYear();
      this.defaultFutureFederalTax = rate.toString();
      if (!wasOverridden) {
        this._futureFederalTax = rate;
        this.sharedInfoService.setFutureFederalTaxRate(rate);
      }
      return;
    }

    const wasOverridden = this.currentFederalTaxOverridden;
    const rate = this.sharedInfoService.getCurrentTaxRateBasedOnCurrentYear();
    this.defaultCurrentFederalTax = rate.toString();
    if (!wasOverridden) {
      this._currentFederalTax = rate;
      this.sharedInfoService.setCurrentFederalTaxRate(rate);
    }
  }

  /**
   * Fills the state tax rate from the TXS state bracket for the scenario's income. A rate the
   * user has overridden is left alone — only the default it reverts to is updated, matching
   * how the federal rate field behaves.
   */
  private applyStateTaxRate(isFuture: boolean): void {
    const rate = this.sharedInfoService.getStateTaxRateBasedOnIncome(isFuture);
    if (rate === null) {
      return;
    }
    if (isFuture) {
      this.applyFutureStateTaxRate(rate);
    } else {
      this.applyCurrentStateTaxRate(rate);
    }
  }

  private applyCurrentStateTaxRate(rate: number): void {
    const wasOverridden = this.currentStateTaxStaticOverridden;
    this.defaultCurrentStateTaxStatic = rate.toString();
    if (wasOverridden) {
      return;
    }
    this._currentStateTaxStatic = rate;
    this.sharedInfoService.setCurrentStateTaxStatic(rate);
    this.sharedInfoService.setCurrentStateTaxRate(rate + Number(this._currentLocalTax));
  }

  private applyFutureStateTaxRate(rate: number): void {
    const wasOverridden = this.futureStateTaxStaticOverridden;
    this.defaultFutureStateTaxStatic = rate.toString();
    if (wasOverridden) {
      return;
    }
    this._futureStateTaxStatic = rate;
    this.sharedInfoService.setFutureStateTaxStatic(rate);
    this.sharedInfoService.setFutureStateTaxRate(rate + Number(this._futureLocalTax));
  }

  get currentStateFlags(): StateTaxFlags {
    return this._currentStateTaxFlags;
  }

  get futureStateFlags(): StateTaxFlags {
    return this._futureStateTaxFlags;
  }

  // Progressive-only highlights paragraph 1; no-Roth-tax and the combined case both highlight paragraph 3.
  get currentStateHighlightId(): string {
    return this.currentStateFlags.iraTaxable
      ? 'sheet-field-state-tax-rate-progressive'
      : 'sheet-field-state-tax-rate-no-tax';
  }

  // The future note is progressive-only, so it always highlights the progressive topic.
  get futureStateHighlightId(): string {
    return 'sheet-field-future-state-tax-rate-progressive';
  }

  get currentStateTaxStaticOverridden(): boolean {
    return Number(this._currentStateTaxStatic) !== Number(this.defaultCurrentStateTaxStatic);
  }

  get futureStateTaxStaticOverridden(): boolean {
    return Number(this._futureStateTaxStatic) !== Number(this.defaultFutureStateTaxStatic);
  }

  revertCurrentStateTaxStatic(): void {
    const rate = Number(this.defaultCurrentStateTaxStatic);
    this._currentStateTaxStatic = rate;
    // The service stores state and local tax summed, so preserve the local part.
    this.sharedInfoService.setCurrentStateTaxStatic(rate);
    this.sharedInfoService.setCurrentStateTaxRate(rate + Number(this._currentLocalTax));
    this.adobeLaunchService.captureAction('revert-current-state-tax', 'link');
  }

  revertFutureStateTaxStatic(): void {
    const rate = Number(this.defaultFutureStateTaxStatic);
    this._futureStateTaxStatic = rate;
    this.sharedInfoService.setFutureStateTaxStatic(rate);
    this.sharedInfoService.setFutureStateTaxRate(rate + Number(this._futureLocalTax));
    this.adobeLaunchService.captureAction('revert-future-state-tax', 'link');
  }
}
